import { useMutation } from '@apollo/client';
import useLocalState from '@phntms/use-local-state';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { BulkMessagesList } from '../../compositions/bulk-messages/list/bulk-messages-list/bulk-messages-list';
import {
  BulkMessageModal,
  BulkMessageModalAction,
} from '../../compositions/bulk-messages/modals/bulk-message-modal/bulk-message-modal';
import { BulkMessagePatientsModal } from '../../compositions/bulk-messages/modals/bulk-message-patients-modal/bulk-message-patients-modal';
import { BulkMessageSlotsModal } from '../../compositions/bulk-messages/modals/bulk-message-slots-modal/bulk-message-slots-modal';
import { useAppContext } from '../../data/context/app-context';
import { useBulkMessageItems } from '../../data/data/bulk-messages/use-bulk-message-items';
import {
  BaseBulkItemDataFragment,
  GetBulkItemsDocument,
  SendBulkMessageInviteToAllDocument,
} from '../../data/types/api/graphql';
import { AlertMessageType } from '../../elements/alert-message/alert-message';
import { Button, ButtonIcon } from '../../elements/button/button';
import { Content } from '../../elements/content/content';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import {
  DataTableLocationState,
  DataTablePageSize,
} from '../../elements/table/data-table/data-table';
import {
  Pagination,
  PaginationData,
} from '../../elements/table/pagination/pagination';
import { Tabs } from '../../elements/tabs/tabs';

export function BulkMessagesPage() {
  const intl = useIntl();
  const { showAlertMessage } = useAppContext();
  const [bulkMessageModalOpen, setBulkMessageModalOpen] =
    useState<boolean>(false);

  const [bulkMessageSlotsModalOpen, setBulkMessageSlotsModalOpen] =
    useState<boolean>(false);
  const [bulkMessagePatientsModalOpen, setBulkMessagePatientsModalOpen] =
    useState<boolean>(false);

  const [selectedItem, setSelectedItem] =
    useState<BaseBulkItemDataFragment | null>(null);

  const [status, setStatus] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');

  const { healthProviderId } = useAppContext();

  const location = useLocation();

  const [limit, setLimit] = useLocalState<number>(
    'dtBulkMessagesLimit',
    DataTablePageSize
  );

  const [paging, setPaging] = useState<PaginationData>({
    page: (location?.state as DataTableLocationState)?.page ?? 1,
    total: 0,
  });

  const [query, setQuery] = useState<string>(
    (location?.state as DataTableLocationState)?.query ?? ''
  );

  const onHandleSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleLimit = useCallback(
    (value: number) => {
      setLimit(value);

      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    [setLimit]
  );

  const onHandlePagination = useCallback((value: number) => {
    setPaging(prevState => ({
      ...prevState,
      page: value,
    }));
  }, []);

  const { data: bulkMessageItemsData, loading: bulkMessageItemsDataLoading } =
    useBulkMessageItems({
      active: status === 'ACTIVE',
      healthProviderId,
      onlyCurrent: false,
      take: limit,
      skip: (paging.page - 1) * limit,
      search: query,
    });

  const bulkItems = useMemo(() => {
    const total = bulkMessageItemsData?.totalCount ?? 0;

    if (bulkMessageItemsData) {
      setPaging(prevState => ({
        ...prevState,
        total,
      }));
    }

    return bulkMessageItemsData?.items ?? [];
  }, [bulkMessageItemsData]);

  useEffect(() => {
    if (query.length > 0) {
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [query]);

  const onHandleItemClick = useCallback((item: BaseBulkItemDataFragment) => {
    setSelectedItem(item);
    setBulkMessageModalOpen(true);
  }, []);

  const onHandleAddPatients = useCallback((item: BaseBulkItemDataFragment) => {
    setSelectedItem(item);
    setBulkMessagePatientsModalOpen(true);
  }, []);

  const onHandleAddSlots = useCallback((item: BaseBulkItemDataFragment) => {
    setSelectedItem(item);
    setBulkMessageSlotsModalOpen(true);
  }, []);

  const [sendBulkMessageToAll, { loading: sendBulkMessageToAllLoading }] =
    useMutation(SendBulkMessageInviteToAllDocument, {
      refetchQueries: [GetBulkItemsDocument],
    });

  const onHandleSendAll = useCallback(
    async (item: BaseBulkItemDataFragment) => {
      try {
        const result = await sendBulkMessageToAll({
          variables: { bulkId: item.id, resend: false },
        });
        if (result.data?.bulkCampaignSendInviteToAll.int) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'agenda.appointment-create.success',
            }),
          });

          // onSaved?.();

          // onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [intl, sendBulkMessageToAll, showAlertMessage]
  );

  const onHandleStatus = useCallback((value: 'ACTIVE' | 'ARCHIVED') => {
    setStatus(value);
  }, []);

  return (
    <>
      <Sidebar
        title={<CustomFormattedMessage id="bulk-messages.title" />}
        toolbar={
          <Button
            icon={ButtonIcon.USER_GROUP}
            onClick={() => {
              setSelectedItem(null);
              setBulkMessageModalOpen(true);
            }}
          >
            <CustomFormattedMessage id="bulk-messages.button.create-bulk-message" />
          </Button>
        }
      />
      <Content
        toolbar={
          <>
            <Tabs<'ACTIVE' | 'ARCHIVED'>
              value={status}
              items={[
                {
                  key: 'ACTIVE',
                  title: <CustomFormattedMessage id="bulk-messages.active" />,
                },
                {
                  key: 'ARCHIVED',
                  title: <CustomFormattedMessage id="bulk-messages.archived" />,
                },
              ]}
              onChange={onHandleStatus}
            />
            <InputField
              icon={BaseInputIcon.SEARCH}
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'common.bulk-messages-search-placeholders',
              })}
              onChange={value => onHandleSearchDebounce(value)}
            />
          </>
        }
      >
        <BulkMessagesList
          items={bulkItems}
          dataLoading={bulkMessageItemsDataLoading}
          loading={sendBulkMessageToAllLoading}
          onClick={onHandleItemClick}
          onAddPatients={onHandleAddPatients}
          onAddSlots={onHandleAddSlots}
          onSendAll={onHandleSendAll}
        />
        <Pagination
          limit={limit}
          paging={paging}
          onChangeLimit={onHandleLimit}
          onChangePage={onHandlePagination}
        />
      </Content>
      {bulkMessageModalOpen && (
        <BulkMessageModal
          id={selectedItem?.id}
          action={
            selectedItem !== null
              ? BulkMessageModalAction.DEFAULT
              : BulkMessageModalAction.UPDATE
          }
          onClose={() => {
            setSelectedItem(null);
            setBulkMessageModalOpen(false);
          }}
        />
      )}
      {bulkMessageSlotsModalOpen && (
        <BulkMessageSlotsModal
          bulkItem={selectedItem}
          onClose={() => {
            setSelectedItem(null);
            setBulkMessageSlotsModalOpen(false);
          }}
        />
      )}
      {bulkMessagePatientsModalOpen && (
        <BulkMessagePatientsModal
          bulkItem={selectedItem}
          onClose={() => {
            setSelectedItem(null);
            setBulkMessagePatientsModalOpen(false);
          }}
        />
      )}
    </>
  );
}
